import React, { useRef, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const MachineHero = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const linkId = 'fontshare-cabinet-grotesk';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = 'https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@400,500,700,800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const leftHandX = useTransform(scrollYProgress, [0, 0.6], ["0%", "-100%"], { clamp: true });
  const rightHandX = useTransform(scrollYProgress, [0, 0.6], ["0%", "100%"], { clamp: true });
  const headerOpacity = useTransform(scrollYProgress, [0.1, 0.6], [0, 1], { clamp: true });
  const headerScale = useTransform(scrollYProgress, [0.1, 0.6], [0.85, 1], { clamp: true });
  const helperOpacity = useTransform(scrollYProgress, [0, 0.2], [0.4, 0], { clamp: true });

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[200vh] bg-white"
      style={{ fontFamily: '"Cabinet Grotesk", sans-serif' }}
    >
      <div className="sticky top-0 left-0 right-0 w-full h-screen flex flex-col justify-between overflow-hidden bg-white selection:bg-neutral-200">
        <div className="h-[44px] w-full shrink-0 pointer-events-none"></div>

        <main className="relative flex-1 w-full flex items-center justify-center overflow-hidden">
          <motion.div
            style={{ opacity: headerOpacity, scale: headerScale, zIndex: 10 }}
            className="text-center flex flex-col justify-center items-center select-none absolute w-full px-4"
          >
            <h1 className="text-[10vw] sm:text-[9vw] md:text-[80px] lg:text-[100px] leading-[1.1] tracking-tight font-medium text-[#000]">
              We build Websites<br />
              <span style={{ color: '#979797' }}>for your Business</span>
            </h1>
          </motion.div>

          <div className="absolute left-0 top-1/2 -translate-y-1/2 flex justify-end origin-right w-1/2 pointer-events-none z-20">
            <motion.div style={{ x: leftHandX }} className="w-full flex justify-end">
              <img
                src="https://cdn.jiro.build/Sumon/Header%20Image/Robot%20Hand.png"
                alt="Robot Hand"
                className="w-full h-auto object-contain max-h-[30vh] sm:max-h-[60vh] object-right"
              />
            </motion.div>
          </div>

          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex justify-start origin-left w-1/2 pointer-events-none z-20">
            <motion.div style={{ x: rightHandX }} className="w-full flex justify-start">
              <img
                src="https://cdn.jiro.build/Sumon/Header%20Image/Human%20Hand.png"
                alt="Human Hand"
                className="w-full h-auto object-contain max-h-[30vh] sm:max-h-[60vh] object-left"
              />
            </motion.div>
          </div>
        </main>

        <footer className="w-full max-w-[1440px] mx-auto flex flex-col md:flex-row justify-between items-start md:items-end gap-4 md:gap-6 z-30 select-none relative px-6 sm:px-10 md:px-12 py-5 md:py-8">
          <div className="max-w-[450px]">
            <p
              className="text-[13px] sm:text-[15px] md:text-[18px] leading-relaxed text-black/80 font-normal"
              style={{ color: '#4a4a4a', lineHeight: 1.4 }}
            >
              We help businesses launch exceptional digital experiences through world-class design, development, and strategy.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3">
            {["Web Design", "Development", "Branding"].map(tag => (
              <div
                key={tag}
                className="flex py-1.5 px-3 md:py-2 md:px-4 rounded-[40px] border border-[#EBEBEB] bg-[#FFF] hover:bg-[#fcfcfc] transition-colors cursor-pointer"
              >
                <span className="text-[12px] md:text-[15px] font-normal text-[#000]">{tag}</span>
              </div>
            ))}
          </div>
        </footer>

        <motion.div
          style={{ opacity: helperOpacity }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-40 pointer-events-none select-none animate-bounce"
        >
          <span className="text-[11px] uppercase tracking-widest font-mono text-neutral-400">Scroll Down</span>
          <svg className="w-4 h-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </motion.div>
      </div>
    </div>
  );
};

export default MachineHero;